import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import { userService } from '@/services/userService';
import type { UserRole } from '@/types/user';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import './LoginPage.css';

// Environment variables
const APP_ENV = import.meta.env.VITE_APP_ENV || import.meta.env.APP_ENV || 'production';
const isLocalEnv = APP_ENV === 'local';

// Types for our API responses
// interface LoginRequest {
//   email: string;
//   password: string;
// }


interface LoginResponse {
  status: string; // "success" or "error"
  message: string;
  data?: {
    token_type: string;
    access_token: string;
    expires_in: number;
    refresh_token: string;
    user: {
      role: UserRole | null | undefined;
      id: number;
      first_name: string;
      last_name?: string;
      email: string;
      fk_role_id?: number;
    };
  };
}

interface ValidationError {
  field: string;
  message: string;
}

interface ApiError {
  message: string;
  errors?: ValidationError[];
  status?: string;
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [apiMessage, setApiMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [redirecting, setRedirecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendOTP, setShowResendOTP] = useState(false);
  const [emailForResend, setEmailForResend] = useState('');
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logedin } = useUser();

  // Get the intended destination or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  const homePath = import.meta.env.VITE_APP_HOME || '/';
  const policyPath = import.meta.env.VITE_APP_POLICY || "/policy";
  const termsPath = import.meta.env.VITE_APP_TERMS || "/terms";

  // Countdown effect for success message
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && redirecting) {
      navigate(from, { 
        replace: true,
        state: { message: apiMessage }
      });
    }
  }, [countdown, redirecting, navigate, from, apiMessage]);

  // Helper to get error for a specific field
  const getFieldError = (fieldName: string) => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  // Clear errors when user starts typing
  const clearFieldError = (fieldName: string) => {
    setErrors(prev => prev.filter(error => error.field !== fieldName));
  };

  // Client-side validation
  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    // Email validation
    if (!email) {
      newErrors.push({ field: 'email', message: 'Email is required' });
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.push({ field: 'email', message: 'Email is invalid' });
    }

    // Password validation
    if (!password) {
      newErrors.push({ field: 'password', message: 'Password is required' });
    } else if (password.length < 6) {
      newErrors.push({ field: 'password', message: 'Password must be at least 6 characters' });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage('');
    setCountdown(0);
    setRedirecting(false);
    setShowResendOTP(false); // Add this state variable
    
    // Clear previous errors
    setErrors([]);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const authResponse = await login(email, password);

      const successMessage =
        authResponse.message || 'Login successful!';
      setApiMessage(successMessage);
      setRedirecting(true);
      setCountdown(3);

    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      if (error.response?.data) {
        const errorData: ApiError = error.response.data;
        
        // Handle validation errors from Laravel
        if (errorData.errors) {
          const validationErrors: ValidationError[] = [];
          Object.keys(errorData.errors).forEach(field => {
            if (errorData?.errors && field in errorData?.errors) {
              errorData?.errors[field].forEach((message: string) => {
                validationErrors.push({ field, message });
              });
            }
          });
          setErrors(validationErrors);
        } else {
          const errorMessage = errorData.message || 'Login failed. Please try again.';
          setApiMessage(errorMessage);
          
          // Check if it's the OTP verification error
          if (errorMessage.includes('Account not active (complete OTP verification)')) {
            setShowResendOTP(true);
            // Store email for resend functionality
            setEmailForResend(email);
          }
        }
      } else if (error.message) {
        setApiMessage(error.message);
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Demo function for testing (using actual API structure)
  const handleDemoLogin = async () => {
    setEmail('learner.free@skillsture.com');
    setPassword('Password123!');
    
    // Simulate API call for demo with your API structure
    setIsLoading(true);
    setApiMessage('');
    setErrors([]);
    setCountdown(0);
    setRedirecting(false);

    setTimeout(() => {
      const demoResponse: LoginResponse = {
        status: "success",
        message: 'Demo login successful! Welcome to Skillsture!',
        data: {
          token_type: 'Bearer',
          access_token: 'demo-jwt-token-12345',
          expires_in: 3600,
          refresh_token: 'demo-refresh-token-12345',
          user: {
            id: 1,
            first_name: 'Demo',
            last_name: 'User',
            email: 'learner.free@skillsture.com',
            fk_role_id: 2,
            role: 'plus',
          }
        }
      };

      // Store tokens using userService to ensure all metadata is set
      userService.updateTokens({
        access_token: demoResponse.data!.access_token,
        refresh_token: demoResponse.data!.refresh_token,
        expires_in: demoResponse.data!.expires_in,
        token_type: demoResponse.data!.token_type,
        user: demoResponse.data!.user as any
      });
      
      // Update user context
      logedin({
        id: demoResponse.data!.user.id,
        email: demoResponse.data!.user.email,
        name: `${demoResponse.data!.user.first_name} ${demoResponse.data!.user.last_name}`.trim(),
        role: demoResponse.data!.user.role,
        first_name: demoResponse.data!.user.first_name
      });

      setApiMessage(demoResponse.message);
      setRedirecting(true);
      setCountdown(3); // Start 3-second countdown
      setIsLoading(false);
    }, 1500);
  };
  

  // Function to handle resend OTP
  const handleResendVerification = async () => {
    if (!emailForResend && !email) {
      setApiMessage('Email is required to resend verification');
      return;
    }

    setIsResendingOTP(true);
    setApiMessage('');

    try {
      // Use the email from state or from the input
      const emailToUse = emailForResend || email;
      
      const response = await api.post('/auth/resend-verification', { 
        email: emailToUse 
      });
      
      const result = response.data as any;

      if (result.status === "success") {
        setApiMessage('Verification email sent! Please check your inbox.');
        setShowResendOTP(false);
        
        // Redirect to verify-email page after a short delay
        setTimeout(() => {
          navigate('/verify-email', { 
            state: { 
              email: emailToUse,
              reset_token: result.data?.reset_token,
              from: 'login'
            }
          });
        }, 2000);
      } else {
        setApiMessage(result.message || 'Failed to resend verification email.');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        setApiMessage(errorData.message || 'Failed to resend verification email.');
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsResendingOTP(false);
    }
  };
  
    // Update body class based on layout
    useEffect(() => {
      const root = document.querySelector('#root');
      root?.classList.add('auth-background');
  
      // Cleanup on unmount
      return () => {
        root?.classList.remove('auth-background');
      };
    }, [location.pathname]);

  return (
    <>
      {/* Logo with badge */}
      <Link to={homePath} className="relative flex md:absolute md:left-24 md:top-24 text-center items-center justify-center content-center">
        <img
          src="/navbar-logo-latest-white.png"
          alt="SKILLSTURE Navbar Logo"
          className="h-16 md:h-20"
          onError={(e) => e.currentTarget.src = '/navbar-logo.png'}
        />
      </Link>
      <div className="login-container min-w-full flex-col">
        <h1 className="login-title">Log In</h1>
        <p className="login-subtitle">Welcome back! Please log in to your account.</p>
        
        {/* Environment indicator for development (optional) */}
        {isLocalEnv && (
          <div className="environment-banner">
            Development Environment - Demo Mode Available
          </div>
        )}
        
        {/* API Message Display */}
        {apiMessage && (
          <div className={`message ${apiMessage.includes('success') ? 'message-success' : 'message-error'}`}>
            <div className="message-content">
              {apiMessage}
              {countdown > 0 && (
                <div className="countdown-text">
                  Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
                </div>
              )}
              
              {/* Show resend OTP link when account is not active */}
              {showResendOTP && (
                <div className="mt-3">
                  <p className="text-sm mb-2">Your account needs email verification to continue.</p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className={`text-orange-500 hover:text-orange-400 p-2 text-sm font-medium ${
                      isResendingOTP ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={isResendingOTP}
                  >
                    {isResendingOTP ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                  <p className="text-xs text-gray-400 mt-1">
                    You'll be redirected to enter the verification code after sending.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="login-card">        
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label text-start">Email</label>
              <input
                id="email"
                type="email"
                className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                  getFieldError('email') ? 'input-error' : ''
                }`}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError('email');
                }}
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />
              {getFieldError('email') && (
                <div className="error-text">{getFieldError('email')}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="form-label text-start">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('password') ? 'input-error' : ''
                  } pr-10`}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError('password');
                  }}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-transparent rounded-full"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {getFieldError('password') && (
                <div className="error-text">{getFieldError('password')}</div>
              )}
            </div>
            
            <div className="forgot-password">
              <a href="/forgot-password" className="forgot-link">Forget Password?</a>
            </div>
            
            <button 
              type="submit" 
              className={`login-button rounded-full ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'Logging In...' : 'Log In'}
            </button>

            {/* Demo login button - Only show in local environment */}
            {isLocalEnv && (
              <button 
                type="button"
                onClick={handleDemoLogin}
                className="demo-button rounded-full"
                disabled={isLoading}
              >
                Try Demo Login
              </button>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-white text-sm">
              Don't have an account?{' '}
              <Link to="/signup" className="text-orange-500 hover:text-orange-400 underline">
                Sign up
              </Link>
            </p>
            <p className="text-gray-400 text-xs mt-2">
              By logging in, you agree to our{' '}
              <a href={termsPath} target="_blank" className="text-orange-500 hover:text-orange-400 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={policyPath} target="_blank" className="text-orange-500 hover:text-orange-400 underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;